import { useCallback, useEffect } from 'react';
import {
  Appearance, Image, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { router, type ErrorBoundaryProps } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { darkTheme, lightTheme, spacing } from '@tabbio/tokens';
import { captureSentryException } from '../../lib/sentry';
import {
  claimWebChunkReloadSlot,
  isWebChunkLoadError,
} from '../../lib/web-chunk-reload';

// Tabbio brand mark: dark glyph on light backgrounds, light glyph on dark.
const brandMarkLight = require('../../../assets/branding/icon-black.png');
const brandMarkDark = require('../../../assets/branding/icon-white.png');

/**
 * App-level Expo Router error boundary. Re-exported as `ErrorBoundary` from the
 * root `_layout.tsx` / `_layout.web.tsx` so a render error on ANY route degrades
 * to a branded, recoverable fallback instead of an unrecoverable white screen.
 *
 * Expo Router renders this OUTSIDE the root layout's providers, so it must be
 * self-contained: colors come from `Appearance` (not ThemeProvider), the brand
 * mark is a static asset, and strings fall back to English if i18n is unavailable.
 */
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  const { t } = useTranslation('common');
  const isDark = Appearance.getColorScheme() === 'dark';
  const theme = isDark ? darkTheme : lightTheme;
  const isChunkLoadError =
    Platform.OS === 'web' && isWebChunkLoadError(error);

  useEffect(() => {
    if (
      isChunkLoadError &&
      typeof window !== 'undefined' &&
      claimWebChunkReloadSlot()
    ) {
      window.location.reload();
      return;
    }

    console.error('[RootErrorBoundary]', error);
    captureSentryException(error, {
      boundary: 'root',
      errorKind: isChunkLoadError ? 'web_chunk_load' : 'render_error',
    });
  }, [error, isChunkLoadError]);

  const handleRetry = useCallback(() => {
    if (isChunkLoadError && Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }

    void retry();
  }, [isChunkLoadError, retry]);

  const handleGoHome = useCallback(() => {
    // On web a hard navigation to the root guarantees a clean app reload.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign('/');
      return;
    }
    // On native, clear the boundary (re-mounts the navigator) then route home.
    void retry().then(() => router.replace('/'));
  }, [retry]);

  return (
    <View style={[styles.screen, { backgroundColor: theme.background }]}>
      <View style={styles.content}>
        <Image
          source={isDark ? brandMarkDark : brandMarkLight}
          style={styles.brandMark}
          resizeMode="contain"
          accessibilityRole="image"
          accessibilityLabel="Tabbio"
        />
        <Text style={[styles.title, { color: theme.text }]}>
          {t('errorBoundary.title', { defaultValue: 'Something went wrong' })}
        </Text>
        <Text style={[styles.message, { color: theme.textSecondary }]}>
          {t('errorBoundary.message', {
            defaultValue:
              'The app ran into an unexpected error. You can try again.',
          })}
        </Text>
        {__DEV__ && error?.message ? (
          <Text style={[styles.devMessage, { color: theme.textTertiary }]}>
            {error.message}
          </Text>
        ) : null}

        <View style={styles.actions}>
          <Pressable
            accessibilityRole="button"
            onPress={handleRetry}
            style={({ pressed }) => [
              styles.primaryButton,
              { backgroundColor: theme.buttonPrimaryBg, opacity: pressed ? 0.85 : 1 },
            ]}
          >
            <Text style={[styles.primaryLabel, { color: theme.buttonPrimaryText }]}>
              {t('errorBoundary.retry', { defaultValue: 'Try again' })}
            </Text>
          </Pressable>

          <Pressable
            accessibilityRole="button"
            onPress={handleGoHome}
            style={({ pressed }) => [
              styles.secondaryButton,
              { opacity: pressed ? 0.6 : 1 },
            ]}
          >
            <Text style={[styles.secondaryLabel, { color: theme.textSecondary }]}>
              {t('errorBoundary.goHome', { defaultValue: 'Go to home' })}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing[6],
  },
  content: {
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
  },
  brandMark: {
    width: 48,
    height: 48,
    marginBottom: spacing[5],
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: spacing[2],
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
  },
  devMessage: {
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    marginTop: spacing[2],
  },
  actions: {
    width: '100%',
    alignItems: 'stretch',
    marginTop: spacing[6],
    gap: spacing[2],
  },
  primaryButton: {
    paddingVertical: 15,
    paddingHorizontal: spacing[6],
    borderRadius: 14,
    alignItems: 'center',
  },
  primaryLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  secondaryLabel: {
    fontSize: 15,
    fontWeight: '500',
  },
});
